import { useState } from "react";
import { Menu, AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal } from "lucide-react";
import { FONTS, EFEITOS, DIRECOES, METRICAS, METRICAS_TWITCH, resolveAnim } from "../../lib/elementStyle";
import { t } from "../../i18n";
import { groupBBox, groupAnimOf, movePatches, scalePatches } from "../../lib/groups";

const VALIGN_OPTS = [{ value: "top", label: t("Topo") }, { value: "middle", label: t("Centro") }, { value: "bottom", label: t("Inferior") }];
const ALIGN_OPTS = [{ value: "left", label: t("Esquerda") }, { value: "center", label: t("Centro") }, { value: "right", label: t("Direita") }];

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <label className="text-xs text-muted-foreground w-24 flex-shrink-0">{t(label)}</label>
      <div className="flex-1 flex items-center gap-1">{children}</div>
    </div>
  );
}

function Num({ value, onChange, min, max, step = 1, testid }) {
  return (
    <input type="number" value={value ?? 0} min={min} max={max} step={step}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-8 bg-input border border-border rounded-sm px-2 text-xs outline-none focus:border-primary"
      data-testid={testid} />
  );
}

function Sel({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full h-8 bg-input border border-border rounded-sm px-1 text-xs outline-none focus:border-primary"
      data-testid={testid}>
      {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  );
}

function Color({ value, onChange, testid }) {
  return (
    <input type="color" value={value || "#ffffff"} onChange={(e) => onChange(e.target.value)}
      className="h-8 w-full bg-input border border-border rounded-sm cursor-pointer" data-testid={testid} />
  );
}

// Seções recolhíveis (como nos painéis do Photoshop/Illustrator). O estado fica salvo
// neste navegador e vale para todos os elementos.
const COLLAPSE_KEY = "lgfx_props_collapsed";
const readCollapsed = () => { try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}"); } catch { return {}; } };

function Section({ title, children }) {
  const [collapsed, setCollapsed] = useState(() => !!readCollapsed()[title]);
  const toggle = () => {
    const v = !collapsed;
    setCollapsed(v);
    try { const all = readCollapsed(); if (v) all[title] = 1; else delete all[title]; localStorage.setItem(COLLAPSE_KEY, JSON.stringify(all)); } catch {}
  };
  return (
    <div className="border-b border-border/60" data-testid={`section-${title}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-[#17181d] hover:bg-[#1d1e24] border-l-2 border-primary/70 text-left transition-colors"
        title={collapsed ? t("Expandir seção") : t("Recolher seção")}
        data-testid={`section-toggle-${title}`}
      >
        <span className="text-[11px] font-mono font-semibold tracking-[0.16em] text-[#8fb7ff]">{t(title)}</span>
        <Menu className={`h-3.5 w-3.5 flex-shrink-0 transition-colors ${collapsed ? "text-muted-foreground" : "text-[#8fb7ff]"}`} />
      </button>
      {!collapsed && <div className="px-3 pt-2 pb-3">{children}</div>}
    </div>
  );
}

function AlignBtn({ onClick, icon: Icon, title, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className="h-8 flex items-center justify-center rounded-sm bg-input border border-border hover:border-primary text-muted-foreground hover:text-foreground transition-colors">
      <Icon className="h-4 w-4" />
    </button>
  );
}

export function PropertiesPanel({ element, onChange, groups = [], fonts = [], dims }) {
  const [novoGrupo, setNovoGrupo] = useState(false);
  if (!element) {
    return <div className="p-6 text-center text-xs text-muted-foreground">{t("Selecione um elemento para editar.")}</div>;
  }
  const el = element;
  const set = (patch, opts) => onChange(el.id, patch, opts);
  const setStyle = (k, v) => set({ estilo: { ...el.estilo, [k]: v } });
  const fontOptions = [...FONTS, ...fonts.filter((f) => !FONTS.includes(f))];
  const anim = resolveAnim(el.animacao);
  const setEntrada = (k, v) => set({ animacao: { entrada: { ...anim.entrada, [k]: v }, saida: anim.saida } }, { sendWs: false });
  const setSaida = (k, v) => set({ animacao: { entrada: anim.entrada, saida: { ...anim.saida, [k]: v } } }, { sendWs: false });
  const setBind = (k, v) => set({ sheetBind: { ...(el.sheetBind || {}), [k]: v } });
  const s = el.estilo || {};
  const alignX = (mode) => {
    const w = dims?.w || 1920, ew = el.largura || 0;
    const x = mode === "left" ? 0 : mode === "center" ? Math.round((w - ew) / 2) : Math.round(w - ew);
    set({ x });
  };
  const alignY = (mode) => {
    const h = dims?.h || 1080, eh = el.altura || 0;
    const y = mode === "top" ? 0 : mode === "middle" ? Math.round((h - eh) / 2) : Math.round(h - eh);
    set({ y });
  };

  return (
    <div className="overflow-y-auto h-full" data-testid="properties-panel">
      <Section title="CAMADA">
        <Row label="Grupo">
          <Sel
            value={novoGrupo ? "__new__" : (el.grupo || "")}
            onChange={(v) => {
              if (v === "__new__") { setNovoGrupo(true); }
              else { setNovoGrupo(false); set({ grupo: v || null }, { sendWs: false }); }
            }}
            options={[{ value: "", label: t("Sem grupo") }, ...groups.map((g) => ({ value: g, label: g })), { value: "__new__", label: t("＋ Novo grupo") }]}
            testid="prop-grupo"
          />
        </Row>
        {novoGrupo && (
          <Row label="Novo nome">
            <input
              autoFocus
              placeholder={t("Nome do grupo")}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              onBlur={(e) => { const v = e.target.value.trim(); if (v) set({ grupo: v }, { sendWs: false }); setNovoGrupo(false); }}
              className="w-full h-8 bg-input border border-border rounded-sm px-2 text-xs outline-none focus:border-primary"
              data-testid="prop-grupo-novo"
            />
          </Row>
        )}
      </Section>
      <Section title="TRANSFORMAR">
        <Row label="X / Y"><Num value={el.x} onChange={(v) => set({ x: v }, { sendWs: false })} testid="prop-x" /><Num value={el.y} onChange={(v) => set({ y: v }, { sendWs: false })} testid="prop-y" /></Row>
        <Row label="L / A"><Num value={el.largura} onChange={(v) => set({ largura: v }, { sendWs: false })} testid="prop-w" /><Num value={el.altura} onChange={(v) => set({ altura: v }, { sendWs: false })} testid="prop-h" /></Row>
        <Row label="Rotação"><Num value={el.rotacao} onChange={(v) => set({ rotacao: v })} min={-360} max={360} testid="prop-rot" /></Row>
        <Row label="Opacidade"><Num value={el.opacidade} onChange={(v) => set({ opacidade: v })} min={0} max={100} testid="prop-opacity" /></Row>
        <Row label="Mesclagem"><Sel value={el.blendMode || "normal"} onChange={(v) => set({ blendMode: v })} options={["normal", "multiply", "screen"]} testid="prop-blend" /></Row>
      </Section>

      <Section title="ALINHAR NO CANVAS">
        <div className="text-[10px] text-muted-foreground mb-1">{t("Horizontal")}</div>
        <div className="grid grid-cols-3 gap-1.5">
          <AlignBtn onClick={() => alignX("left")} icon={AlignStartVertical} title={t("Alinhar à esquerda")} testid="align-left" />
          <AlignBtn onClick={() => alignX("center")} icon={AlignCenterVertical} title={t("Centralizar horizontalmente")} testid="align-center-h" />
          <AlignBtn onClick={() => alignX("right")} icon={AlignEndVertical} title={t("Alinhar à direita")} testid="align-right" />
        </div>
        <div className="text-[10px] text-muted-foreground mt-2 mb-1">{t("Vertical")}</div>
        <div className="grid grid-cols-3 gap-1.5">
          <AlignBtn onClick={() => alignY("top")} icon={AlignStartHorizontal} title={t("Alinhar ao topo")} testid="align-top" />
          <AlignBtn onClick={() => alignY("middle")} icon={AlignCenterHorizontal} title={t("Centralizar verticalmente")} testid="align-middle" />
          <AlignBtn onClick={() => alignY("bottom")} icon={AlignEndHorizontal} title={t("Alinhar à base")} testid="align-bottom" />
        </div>
        <button onClick={() => { alignX("center"); alignY("middle"); }} className="w-full mt-2 h-8 rounded-sm bg-primary/90 text-primary-foreground text-xs font-semibold hover:bg-primary transition-colors" data-testid="align-center-both">
          {t("Centralizar no canvas")}
        </button>
      </Section>

      {el.tipo === "texto" && (
        <>
          <Section title="CONTEÚDO">
            <textarea value={el.conteudo || ""} onChange={(e) => set({ conteudo: e.target.value })}
              rows={3} className="w-full bg-input border border-border rounded-sm p-2 text-xs outline-none focus:border-primary resize-none"
              data-testid="prop-text-content" />
          </Section>
          <Section title="TIPOGRAFIA">
            <Row label="Fonte"><Sel value={s.fontFamily} onChange={(v) => setStyle("fontFamily", v)} options={fontOptions} testid="prop-font" /></Row>
            <Row label="Tamanho"><Num value={s.fontSize} onChange={(v) => setStyle("fontSize", v)} min={8} max={400} testid="prop-fontsize" /></Row>
            <Row label="Peso"><Sel value={s.fontWeight} onChange={(v) => setStyle("fontWeight", Number(v))} options={[{ value: 400, label: "Regular" }, { value: 600, label: "Semibold" }, { value: 700, label: "Bold" }, { value: 800, label: "ExtraBold" }]} testid="prop-weight" /></Row>
            <Row label="Cor"><Color value={s.color} onChange={(v) => setStyle("color", v)} testid="prop-color" /></Row>
            <Row label="Alinhar"><Sel value={s.textAlign} onChange={(v) => setStyle("textAlign", v)} options={ALIGN_OPTS} testid="prop-align" /></Row>
            <Row label="Alinhar vert."><Sel value={s.verticalAlign || "middle"} onChange={(v) => setStyle("verticalAlign", v)} options={VALIGN_OPTS} testid="prop-valign" /></Row>
            <Row label="Altura linha"><Num value={s.lineHeight} onChange={(v) => setStyle("lineHeight", v)} step={0.1} min={0.5} max={3} testid="prop-lineheight" /></Row>
            <Row label="Espaçamento"><Num value={s.letterSpacing} onChange={(v) => setStyle("letterSpacing", v)} min={-10} max={40} testid="prop-letterspacing" /></Row>
          </Section>
          <Section title="CONTORNO & SOMBRA">
            <Row label="Contorno"><Num value={s.strokeWidth} onChange={(v) => setStyle("strokeWidth", v)} min={0} max={30} testid="prop-strokew" /><Color value={s.strokeColor} onChange={(v) => setStyle("strokeColor", v)} testid="prop-strokec" /></Row>
            <Row label="Sombra X/Y"><Num value={s.shadowX} onChange={(v) => setStyle("shadowX", v)} testid="prop-shadowx" /><Num value={s.shadowY} onChange={(v) => setStyle("shadowY", v)} testid="prop-shadowy" /></Row>
            <Row label="Sombra blur"><Num value={s.shadowBlur} onChange={(v) => setStyle("shadowBlur", v)} min={0} max={60} testid="prop-shadowblur" /></Row>
          </Section>
          <Section title="FUNDO DO BLOCO">
            <Row label="Cor"><Color value={s.bgColor} onChange={(v) => setStyle("bgColor", v)} testid="prop-bgcolor" /></Row>
            <Row label="Opacidade"><Num value={s.bgOpacity} onChange={(v) => setStyle("bgOpacity", v)} min={0} max={100} testid="prop-bgopacity" /></Row>
            <Row label="Padding"><Num value={s.padding} onChange={(v) => setStyle("padding", v)} min={0} max={100} testid="prop-padding" /></Row>
            <Row label="Raio borda"><Num value={s.borderRadius} onChange={(v) => setStyle("borderRadius", v)} min={0} max={100} testid="prop-radius" /></Row>
          </Section>
          <Section title="FUNÇÃO AO VIVO">
            <Row label="Função">
              <Sel
                value={el.papel || "none"}
                onChange={(v) => {
                  const patch = { papel: v === "none" ? null : v };
                  if (v === "cronometro" && !el.timer) patch.timer = { duration: 300, remaining: 300, running: false, startedAt: null };
                  set(patch, { sendWs: false });
                }}
                options={[{ value: "none", label: t("Nenhuma") }, { value: "placar", label: t("Placar (+/-)") }, { value: "cronometro", label: t("Cronômetro") }]}
                testid="prop-papel"
              />
            </Row>
          </Section>
        </>
      )}

      {el.tipo === "media" && (
        <Section title="MÍDIA">
          <Row label="Loop">
            <input type="checkbox" checked={el.loop !== false} onChange={(e) => set({ loop: e.target.checked })} className="accent-primary h-4 w-4" data-testid="prop-loop" />
          </Row>
          <Row label="Autoplay">
            <input type="checkbox" checked={el.autoplay !== false} onChange={(e) => set({ autoplay: e.target.checked })} className="accent-primary h-4 w-4" data-testid="prop-autoplay" />
          </Row>
        </Section>
      )}

      {el.tipo === "dynamic" && (
        <>
          <Section title={el.platform === "twitch" ? "DADO DA TWITCH" : "DADO DO YOUTUBE"}>
            <Row label="Plataforma">
              <Sel
                value={el.platform || "youtube"}
                onChange={(v) => {
                  const patch = { platform: v === "youtube" ? null : v };
                  const valid = (v === "twitch" ? METRICAS_TWITCH : METRICAS).map((m) => m.value);
                  if (!valid.includes(el.metrica)) patch.metrica = "viewers";
                  set(patch);
                }}
                options={[{ value: "youtube", label: "YouTube" }, { value: "twitch", label: "Twitch" }]}
                testid="prop-dyn-platform"
              />
            </Row>
            <Row label="Métrica"><Sel value={el.metrica} onChange={(v) => set({ metrica: v })} options={el.platform === "twitch" ? METRICAS_TWITCH : METRICAS} testid="prop-yt-metric" /></Row>
            {el.metrica !== "duration" && (
              <Row label="Formato"><Sel value={el.formato || "abbrev"} onChange={(v) => set({ formato: v })} options={[{ value: "abbrev", label: t("Abreviado (1.2k)") }, { value: "raw", label: t("Completo (1.234)") }]} testid="prop-yt-format" /></Row>
            )}
            <Row label="Modelo"><input value={el.template || "{n}"} onChange={(e) => set({ template: e.target.value })} className="w-full h-8 bg-input border border-border rounded-sm px-2 text-xs outline-none focus:border-primary" placeholder={t("Ex: {n} assistindo")} data-testid="prop-yt-template" /></Row>
            <p className="text-[10px] text-muted-foreground mt-1">{t("Use")} <code>{"{n}"}</code> {t("onde o número deve aparecer.")}</p>
            {el.metrica !== "duration" && (
              <Row label="Exibição"><Sel value={el.modo || "texto"} onChange={(v) => set({ modo: v })} options={[{ value: "texto", label: t("Texto") }, { value: "barra", label: t("Barra de meta") }]} testid="prop-yt-mode" /></Row>
            )}
            {el.modo === "barra" && el.metrica !== "duration" && (
              <>
                <Row label="Meta"><Num value={el.meta} onChange={(v) => set({ meta: v })} min={1} testid="prop-yt-goal" /></Row>
                <Row label="Cor barra"><Color value={s.barColor} onChange={(v) => setStyle("barColor", v)} testid="prop-yt-barcolor" /></Row>
                <Row label="Fundo barra"><Color value={s.barBg} onChange={(v) => setStyle("barBg", v)} testid="prop-yt-barbg" /></Row>
              </>
            )}
          </Section>
          <Section title="TIPOGRAFIA">
            <Row label="Fonte"><Sel value={s.fontFamily} onChange={(v) => setStyle("fontFamily", v)} options={fontOptions} testid="prop-yt-font" /></Row>
            <Row label="Tamanho"><Num value={s.fontSize} onChange={(v) => setStyle("fontSize", v)} min={8} max={400} testid="prop-yt-fontsize" /></Row>
            <Row label="Peso"><Sel value={s.fontWeight} onChange={(v) => setStyle("fontWeight", Number(v))} options={[{ value: 400, label: "Regular" }, { value: 600, label: "Semibold" }, { value: 700, label: "Bold" }, { value: 800, label: "ExtraBold" }]} testid="prop-yt-weight" /></Row>
            <Row label="Cor"><Color value={s.color} onChange={(v) => setStyle("color", v)} testid="prop-yt-color" /></Row>
            <Row label="Alinhar"><Sel value={s.textAlign} onChange={(v) => setStyle("textAlign", v)} options={ALIGN_OPTS} testid="prop-yt-align" /></Row>
            <Row label="Alinhar vert."><Sel value={s.verticalAlign || "middle"} onChange={(v) => setStyle("verticalAlign", v)} options={VALIGN_OPTS} testid="prop-yt-valign" /></Row>
          </Section>
        </>
      )}

      {el.tipo === "chat" && (
        <>
          <Section title="CAIXA DE CHAT">
            <Row label="Máx. mensagens"><Num value={el.maxMensagens} onChange={(v) => set({ maxMensagens: v })} min={1} max={20} testid="prop-chat-max" /></Row>
            <Row label="Mostrar avatar"><input type="checkbox" checked={el.mostrarAvatar !== false} onChange={(e) => set({ mostrarAvatar: e.target.checked })} className="accent-primary h-4 w-4" data-testid="prop-chat-avatar" /></Row>
          </Section>
          <Section title="TIPOGRAFIA">
            <Row label="Fonte"><Sel value={s.fontFamily} onChange={(v) => setStyle("fontFamily", v)} options={fontOptions} testid="prop-chat-font" /></Row>
            <Row label="Tamanho"><Num value={s.fontSize} onChange={(v) => setStyle("fontSize", v)} min={10} max={120} testid="prop-chat-fontsize" /></Row>
            <Row label="Cor texto"><Color value={s.color} onChange={(v) => setStyle("color", v)} testid="prop-chat-color" /></Row>
            <Row label="Cor autor"><Color value={s.authorColor} onChange={(v) => setStyle("authorColor", v)} testid="prop-chat-authorcolor" /></Row>
          </Section>
          <Section title="FUNDO">
            <Row label="Cor"><Color value={s.bgColor} onChange={(v) => setStyle("bgColor", v)} testid="prop-chat-bg" /></Row>
            <Row label="Opacidade"><Num value={s.bgOpacity} onChange={(v) => setStyle("bgOpacity", v)} min={0} max={100} testid="prop-chat-bgop" /></Row>
            <Row label="Raio"><Num value={s.borderRadius} onChange={(v) => setStyle("borderRadius", v)} min={0} max={60} testid="prop-chat-radius" /></Row>
            <Row label="Padding"><Num value={s.padding} onChange={(v) => setStyle("padding", v)} min={0} max={80} testid="prop-chat-padding" /></Row>
          </Section>
        </>
      )}

      {el.tipo === "comment" && (
        <Section title="COMENTÁRIO EM DESTAQUE">
          <Row label="Fonte"><Sel value={s.fontFamily} onChange={(v) => setStyle("fontFamily", v)} options={fontOptions} testid="prop-cmt-font" /></Row>
          <Row label="Tamanho"><Num value={s.fontSize} onChange={(v) => setStyle("fontSize", v)} min={12} max={140} testid="prop-cmt-fontsize" /></Row>
          <Row label="Cor texto"><Color value={s.color} onChange={(v) => setStyle("color", v)} testid="prop-cmt-color" /></Row>
          <Row label="Cor autor"><Color value={s.authorColor} onChange={(v) => setStyle("authorColor", v)} testid="prop-cmt-authorcolor" /></Row>
          <Row label="Fundo"><Color value={s.bgColor} onChange={(v) => setStyle("bgColor", v)} testid="prop-cmt-bg" /></Row>
          <Row label="Opac. fundo"><Num value={s.bgOpacity} onChange={(v) => setStyle("bgOpacity", v)} min={0} max={100} testid="prop-cmt-bgop" /></Row>
          <Row label="Raio"><Num value={s.borderRadius} onChange={(v) => setStyle("borderRadius", v)} min={0} max={60} testid="prop-cmt-radius" /></Row>
        </Section>
      )}

      {el.tipo === "superchat" && (
        <Section title="ALERTA DE SUPER CHAT">
          <Row label="Duração (s)"><Num value={el.duracao} onChange={(v) => set({ duracao: v })} min={2} max={30} testid="prop-sc-dur" /></Row>
          <Row label="Fonte"><Sel value={s.fontFamily} onChange={(v) => setStyle("fontFamily", v)} options={fontOptions} testid="prop-sc-font" /></Row>
          <Row label="Tamanho"><Num value={s.fontSize} onChange={(v) => setStyle("fontSize", v)} min={12} max={100} testid="prop-sc-fontsize" /></Row>
          <Row label="Cor fundo"><Color value={s.bgColor} onChange={(v) => setStyle("bgColor", v)} testid="prop-sc-bg" /></Row>
          <Row label="Raio"><Num value={s.borderRadius} onChange={(v) => setStyle("borderRadius", v)} min={0} max={60} testid="prop-sc-radius" /></Row>
        </Section>
      )}

      {el.tipo === "forma" && (
        <Section title="FORMA">
          <Row label="Tipo"><Sel value={el.variante || "circulo"} onChange={(v) => set({ variante: v })} options={[{ value: "circulo", label: t("Círculo") }, { value: "triangulo", label: t("Triângulo") }, { value: "estrela", label: t("Estrela") }, { value: "retangulo", label: t("Retângulo") }, { value: "losango", label: t("Losango") }]} testid="prop-shape-variant" /></Row>
          <Row label="Preenchimento"><Color value={s.fillColor} onChange={(v) => setStyle("fillColor", v)} testid="prop-shape-fill" /></Row>
          <Row label="Cor borda"><Color value={s.strokeColor} onChange={(v) => setStyle("strokeColor", v)} testid="prop-shape-strokec" /></Row>
          <Row label="Espessura"><Num value={s.strokeWidth} onChange={(v) => setStyle("strokeWidth", v)} min={0} max={60} testid="prop-shape-strokew" /></Row>
          {el.variante === "retangulo" && (
            <Row label="Cantos"><Num value={s.cornerRadius} onChange={(v) => setStyle("cornerRadius", v)} min={0} max={400} testid="prop-shape-radius" /></Row>
          )}
          <Row label="Sombra"><input type="checkbox" checked={!!s.shadowEnabled} onChange={(e) => setStyle("shadowEnabled", e.target.checked)} className="accent-primary h-4 w-4" data-testid="prop-shape-shadow" /></Row>
          {s.shadowEnabled && (
            <>
              <Row label="Cor sombra"><Color value={s.shadowColor} onChange={(v) => setStyle("shadowColor", v)} testid="prop-shape-shadowcolor" /></Row>
              <Row label="Opac. sombra"><Num value={s.shadowOpacity} onChange={(v) => setStyle("shadowOpacity", v)} min={0} max={100} testid="prop-shape-shadowop" /></Row>
              <Row label="Sombra X/Y"><Num value={s.shadowX} onChange={(v) => setStyle("shadowX", v)} testid="prop-shape-shadowx" /><Num value={s.shadowY} onChange={(v) => setStyle("shadowY", v)} testid="prop-shape-shadowy" /></Row>
              <Row label="Sombra blur"><Num value={s.shadowBlur} onChange={(v) => setStyle("shadowBlur", v)} min={0} max={100} testid="prop-shape-shadowblur" /></Row>
            </>
          )}
        </Section>
      )}

      {(el.tipo === "texto" || el.tipo === "media") && (
        <Section title="GOOGLE SHEETS">
          <Row label="Vincular"><input type="checkbox" checked={!!el.sheetBind?.enabled} onChange={(e) => setBind("enabled", e.target.checked)} className="accent-primary h-4 w-4" data-testid="prop-sheet-enable" /></Row>
          {el.sheetBind?.enabled && (
            <>
              <Row label="Célula/Intervalo"><input value={el.sheetBind?.range || ""} onChange={(e) => setBind("range", e.target.value)} placeholder={t("A1 ou A2:A20")} className="w-full h-8 bg-input border border-border rounded-sm px-2 text-xs outline-none focus:border-primary" data-testid="prop-sheet-range" /></Row>
              <Row label="Modo"><Sel value={el.sheetBind?.mode || "cell"} onChange={(v) => setBind("mode", v)} options={[{ value: "cell", label: t("Célula única") }, { value: "rotate", label: t("Rotacionar lista") }, { value: "stack", label: t("Empilhar lista") }]} testid="prop-sheet-mode" /></Row>
              {el.sheetBind?.mode === "rotate" && <Row label="Intervalo (s)"><Num value={el.sheetBind?.intervalo ?? 5} onChange={(v) => setBind("intervalo", v)} min={1} max={120} testid="prop-sheet-interval" /></Row>}
              {el.sheetBind?.mode === "stack" && <Row label="Máx. linhas"><Num value={el.sheetBind?.maxLinhas ?? 5} onChange={(v) => setBind("maxLinhas", v)} min={1} max={30} testid="prop-sheet-max" /></Row>}
              <p className="text-[10px] text-muted-foreground mt-1">{el.tipo === "media" ? t("A célula deve conter a URL de uma imagem.") : t("Conecte a planilha no botão \"Planilha\" do topo.")}</p>
            </>
          )}
        </Section>
      )}

      <Section title="ANIMAÇÃO DE ENTRADA">
        <Row label="Efeito"><Sel value={anim.entrada.efeito} onChange={(v) => setEntrada("efeito", v)} options={EFEITOS} testid="prop-anim-in-efeito" /></Row>
        {["slide", "wipe", "flip"].includes(anim.entrada.efeito) && <Row label="Direção"><Sel value={anim.entrada.direcao} onChange={(v) => setEntrada("direcao", v)} options={DIRECOES} testid="prop-anim-in-direcao" /></Row>}
        <Row label="Duração (ms)"><Num value={anim.entrada.duracao} onChange={(v) => setEntrada("duracao", v)} min={0} max={5000} step={50} testid="prop-anim-in-dur" /></Row>
        <Row label="Delay (ms)"><Num value={anim.entrada.delay} onChange={(v) => setEntrada("delay", v)} min={0} max={5000} step={50} testid="prop-anim-in-delay" /></Row>
      </Section>

      <Section title="ANIMAÇÃO DE SAÍDA">
        <Row label="Efeito"><Sel value={anim.saida.efeito} onChange={(v) => setSaida("efeito", v)} options={EFEITOS} testid="prop-anim-out-efeito" /></Row>
        {["slide", "wipe", "flip"].includes(anim.saida.efeito) && <Row label="Direção"><Sel value={anim.saida.direcao} onChange={(v) => setSaida("direcao", v)} options={DIRECOES} testid="prop-anim-out-direcao" /></Row>}
        <Row label="Duração (ms)"><Num value={anim.saida.duracao} onChange={(v) => setSaida("duracao", v)} min={0} max={5000} step={50} testid="prop-anim-out-dur" /></Row>
      </Section>
    </div>
  );
}

// Painel da SELEÇÃO MÚLTIPLA (Shift+clique): mover em bloco, alinhar entre as camadas e no canvas, ocultar/bloquear/excluir.
export function MultiPanel({ members, dims, onChangeMany, onClear, onDelete }) {
  const bb = groupBBox(members);
  const W = dims?.w || 1920, H = dims?.h || 1080;
  const locked = members.some((m) => m.travado);
  const allHidden = members.every((m) => m.visivel === false);
  const allLocked = members.every((m) => m.travado);
  const move = (x, y) => onChangeMany(movePatches(members, x - bb.x, y - bb.y));
  // Alinhar entre elas: cada camada se alinha à caixa que envolve a seleção.
  const among = (fn) => onChangeMany(Object.fromEntries(members.filter((m) => !m.travado).map((m) => [m.id, fn(m)])));
  const setAll = (patch) => onChangeMany(Object.fromEntries(members.map((m) => [m.id, patch])));
  return (
    <div className="overflow-y-auto h-full" data-testid="multi-panel">
      <div className="px-3 py-3 border-b border-border/60 flex items-center gap-2" style={{ boxShadow: "inset 3px 0 0 #007AFF" }}>
        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0 bg-primary" />
        <span className="flex-1 text-sm font-semibold">{t("{n} camadas selecionadas", { n: members.length })}</span>
        <button onClick={onClear} className="text-[10px] font-mono text-muted-foreground hover:text-foreground" data-testid="multi-clear">{t("Limpar seleção")}</button>
      </div>
      <p className="px-3 pt-2 text-[10px] text-muted-foreground leading-relaxed">{t("Shift+clique adiciona ou tira camadas. Arraste uma delas para mover todas; as setas do menu Camadas ordenam todas juntas.")}</p>
      <Section title="TRANSFORMAR">
        <Row label="X / Y"><Num value={bb.x} onChange={(v) => move(v, bb.y)} testid="multi-x" /><Num value={bb.y} onChange={(v) => move(bb.x, v)} testid="multi-y" /></Row>
        {locked && <p className="text-[10px] text-amber-400 mt-1">{t("Há camadas bloqueadas na seleção: elas não se movem.")}</p>}
      </Section>
      <Section title="ALINHAR ENTRE ELAS">
        <div className="grid grid-cols-3 gap-1.5">
          <AlignBtn onClick={() => among(() => ({ x: bb.x }))} icon={AlignStartVertical} title={t("Alinhar à esquerda")} testid="malign-left" />
          <AlignBtn onClick={() => among((m) => ({ x: Math.round(bb.x + (bb.w - (m.largura || 0)) / 2) }))} icon={AlignCenterVertical} title={t("Centralizar horizontalmente")} testid="malign-center-h" />
          <AlignBtn onClick={() => among((m) => ({ x: Math.round(bb.x + bb.w - (m.largura || 0)) }))} icon={AlignEndVertical} title={t("Alinhar à direita")} testid="malign-right" />
          <AlignBtn onClick={() => among(() => ({ y: bb.y }))} icon={AlignStartHorizontal} title={t("Alinhar ao topo")} testid="malign-top" />
          <AlignBtn onClick={() => among((m) => ({ y: Math.round(bb.y + (bb.h - (m.altura || 0)) / 2) }))} icon={AlignCenterHorizontal} title={t("Centralizar verticalmente")} testid="malign-middle" />
          <AlignBtn onClick={() => among((m) => ({ y: Math.round(bb.y + bb.h - (m.altura || 0)) }))} icon={AlignEndHorizontal} title={t("Alinhar à base")} testid="malign-bottom" />
        </div>
      </Section>
      <Section title="ALINHAR NO CANVAS">
        <p className="text-[10px] text-muted-foreground mb-1.5">{t("Move a seleção inteira, mantendo a posição relativa entre as camadas.")}</p>
        <div className="grid grid-cols-3 gap-1.5">
          <AlignBtn onClick={() => move(0, bb.y)} icon={AlignStartVertical} title={t("Alinhar à esquerda")} testid="mcanvas-left" />
          <AlignBtn onClick={() => move(Math.round((W - bb.w) / 2), bb.y)} icon={AlignCenterVertical} title={t("Centralizar horizontalmente")} testid="mcanvas-center-h" />
          <AlignBtn onClick={() => move(Math.round(W - bb.w), bb.y)} icon={AlignEndVertical} title={t("Alinhar à direita")} testid="mcanvas-right" />
          <AlignBtn onClick={() => move(bb.x, 0)} icon={AlignStartHorizontal} title={t("Alinhar ao topo")} testid="mcanvas-top" />
          <AlignBtn onClick={() => move(bb.x, Math.round((H - bb.h) / 2))} icon={AlignCenterHorizontal} title={t("Centralizar verticalmente")} testid="mcanvas-middle" />
          <AlignBtn onClick={() => move(bb.x, Math.round(H - bb.h))} icon={AlignEndHorizontal} title={t("Alinhar à base")} testid="mcanvas-bottom" />
        </div>
      </Section>
      <Section title="EDITAR">
        <div className="grid grid-cols-2 gap-1.5">
          <button onClick={() => setAll({ visivel: allHidden })} className="h-8 rounded-sm border border-border text-xs hover:bg-secondary transition-colors" data-testid="multi-visible">{allHidden ? t("Mostrar todas") : t("Ocultar todas")}</button>
          <button onClick={() => setAll({ travado: !allLocked })} className="h-8 rounded-sm border border-border text-xs hover:bg-secondary transition-colors" data-testid="multi-lock">{allLocked ? t("Desbloquear todas") : t("Bloquear todas")}</button>
        </div>
        <button onClick={onDelete} className="w-full mt-1.5 h-8 rounded-sm border border-destructive/60 text-destructive text-xs hover:bg-destructive/10 transition-colors" data-testid="multi-delete">{t("Excluir selecionadas")}</button>
      </Section>
    </div>
  );
}

// Painel do GRUPO selecionado: posição/tamanho do conjunto (proporcional) e animação própria do grupo.
export function GroupPanel({ name, members, color, groups = [], dims, onRename, onUngroup, onChangeMany, onAnim }) {
  const bb = groupBBox(members);
  const anim = resolveAnim(groupAnimOf(members));
  const locked = members.some((m) => m.travado);
  const move = (x, y) => onChangeMany(movePatches(members, x - bb.x, y - bb.y));
  const resizeW = (w) => { if (w > 0 && bb.w) onChangeMany(scalePatches(members, bb, { x: bb.x, y: bb.y, w, h: Math.round(bb.h * (w / bb.w)) })); };
  const resizeH = (h) => { if (h > 0 && bb.h) { const w = Math.round(bb.w * (h / bb.h)); onChangeMany(scalePatches(members, bb, { x: bb.x, y: bb.y, w, h })); } };
  const setIn = (k, v) => onAnim({ entrada: { ...anim.entrada, [k]: v }, saida: anim.saida });
  const setOut = (k, v) => onAnim({ entrada: anim.entrada, saida: { ...anim.saida, [k]: v } });
  const W = dims?.w || 1920, H = dims?.h || 1080;
  return (
    <div className="overflow-y-auto h-full" data-testid="group-panel">
      <div className="px-3 py-3 border-b border-border/60 flex items-center gap-2" style={{ boxShadow: `inset 3px 0 0 ${color}` }}>
        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
        <input
          key={name}
          defaultValue={name}
          onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== name) onRename(v); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          className="flex-1 min-w-0 bg-transparent text-sm font-semibold outline-none border-b border-transparent focus:border-primary"
          data-testid="group-name"
        />
        <span className="text-[10px] font-mono text-muted-foreground flex-shrink-0">{t("{n} camadas", { n: members.length })}</span>
      </div>
      <p className="px-3 pt-2 text-[10px] text-muted-foreground leading-relaxed">{t("O grupo é editado como um bloco. Dê um duplo clique numa camada no canvas para editá-la sozinha.")}</p>
      <Section title="TRANSFORMAR">
        <Row label="X / Y"><Num value={bb.x} onChange={(v) => move(v, bb.y)} testid="group-x" /><Num value={bb.y} onChange={(v) => move(bb.x, v)} testid="group-y" /></Row>
        <Row label="L / A"><Num value={bb.w} onChange={resizeW} min={1} testid="group-w" /><Num value={bb.h} onChange={resizeH} min={1} testid="group-h" /></Row>
        <p className="text-[10px] text-muted-foreground mt-1">{t("O tamanho muda de forma proporcional: posições, tamanhos e fontes das camadas acompanham.")}</p>
        {locked && <p className="text-[10px] text-amber-400 mt-1">{t("Há camadas bloqueadas neste grupo: arrastar e redimensionar no canvas estão desativados.")}</p>}
      </Section>
      <Section title="ALINHAR NO CANVAS">
        <div className="grid grid-cols-3 gap-1.5">
          <AlignBtn onClick={() => move(0, bb.y)} icon={AlignStartVertical} title={t("Alinhar à esquerda")} testid="galign-left" />
          <AlignBtn onClick={() => move(Math.round((W - bb.w) / 2), bb.y)} icon={AlignCenterVertical} title={t("Centralizar horizontalmente")} testid="galign-center-h" />
          <AlignBtn onClick={() => move(Math.round(W - bb.w), bb.y)} icon={AlignEndVertical} title={t("Alinhar à direita")} testid="galign-right" />
          <AlignBtn onClick={() => move(bb.x, 0)} icon={AlignStartHorizontal} title={t("Alinhar ao topo")} testid="galign-top" />
          <AlignBtn onClick={() => move(bb.x, Math.round((H - bb.h) / 2))} icon={AlignCenterHorizontal} title={t("Centralizar verticalmente")} testid="galign-middle" />
          <AlignBtn onClick={() => move(bb.x, Math.round(H - bb.h))} icon={AlignEndHorizontal} title={t("Alinhar à base")} testid="galign-bottom" />
        </div>
      </Section>
      <Section title="ANIMAÇÃO DE ENTRADA DO GRUPO">
        <Row label="Efeito"><Sel value={anim.entrada.efeito} onChange={(v) => setIn("efeito", v)} options={EFEITOS} testid="group-anim-in-efeito" /></Row>
        {["slide", "wipe", "flip"].includes(anim.entrada.efeito) && <Row label="Direção"><Sel value={anim.entrada.direcao} onChange={(v) => setIn("direcao", v)} options={DIRECOES} testid="group-anim-in-direcao" /></Row>}
        <Row label="Duração (ms)"><Num value={anim.entrada.duracao} onChange={(v) => setIn("duracao", v)} min={0} max={5000} step={50} testid="group-anim-in-dur" /></Row>
        <Row label="Delay (ms)"><Num value={anim.entrada.delay} onChange={(v) => setIn("delay", v)} min={0} max={5000} step={50} testid="group-anim-in-delay" /></Row>
      </Section>
      <Section title="ANIMAÇÃO DE SAÍDA DO GRUPO">
        <Row label="Efeito"><Sel value={anim.saida.efeito} onChange={(v) => setOut("efeito", v)} options={EFEITOS} testid="group-anim-out-efeito" /></Row>
        {["slide", "wipe", "flip"].includes(anim.saida.efeito) && <Row label="Direção"><Sel value={anim.saida.direcao} onChange={(v) => setOut("direcao", v)} options={DIRECOES} testid="group-anim-out-direcao" /></Row>}
        <Row label="Duração (ms)"><Num value={anim.saida.duracao} onChange={(v) => setOut("duracao", v)} min={0} max={5000} step={50} testid="group-anim-out-dur" /></Row>
        <p className="text-[10px] text-muted-foreground mt-1">{t("A animação do grupo roda ao ligar/desligar o grupo no Ao Vivo, junto com as animações de cada camada.")}</p>
      </Section>
      <div className="p-3">
        <button onClick={onUngroup} className="w-full h-8 rounded-sm border border-border text-xs hover:bg-secondary transition-colors" data-testid="group-ungroup">{t("Desagrupar")}</button>
      </div>
    </div>
  );
}
